import { Dispatch, SetStateAction, useEffect, useState } from 'react';

type UseLocalStorageStateReturnType<T> = [T, Dispatch<SetStateAction<T>>];

export const usePersistLocalStorageState = <T>(
  key: string,
  defaultValue: T,
): UseLocalStorageStateReturnType<T> => {
  const getInitialValue = (): T => {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : defaultValue;
  };

  const [state, setState] = useState<T>(getInitialValue);

  // Update localStorage whenever the state changes
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  // Listen for changes to localStorage from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.newValue) {
        setState(JSON.parse(event.newValue));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [state, setState];
};
